/**
 * V3 — Rapprochement bancaire : croise écritures Sage (BNQ) avec opérations Caisse (Banque).
 * Renvoie les flux des deux sources + écart + pill "rapproché" si écart < 1 FCFA.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api } from './api';
import { GitCompareArrows, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2 } from 'lucide-react';

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('fr-FR');

const ENTITIES = ['ROPS', 'NETBTP', 'CIS', 'NKEYI', 'R2S', 'EXCUISINE'];

export default function V3BankReconciliation() {
  const [entity, setEntity] = useState('ROPS');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = { entity };
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const { data: d } = await api.get('/api/v3/groupe/bank-reconciliation', { params });
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [entity, from, to]);

  useEffect(() => { Promise.resolve().then(fetchData); }, [fetchData]);

  return (
    <div className="v3-page" data-testid="v3-bank-reconciliation">
      <div className="v3-page-head">
        <h1>Rapprochement bancaire</h1>
        <p>Compare les opérations Caisse "Banque" avec les écritures Sage journal "BNQ" pour détecter les écarts.</p>
      </div>

      <section className="v3-filters">
        <div className="v3-filter-grp">
          <label>Entité</label>
          <select value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="filter-entity">
            {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Du</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="v3-filter-grp">
          <label>Au</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </section>

      {err && <div className="v3-error">⚠️ {err}</div>}
      {loading && <div className="v3-empty">Chargement…</div>}

      {data && !loading && (
        <>
          <section className="v3-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <KpiCard icon={<TrendingUp/>} label="Net Caisse (Banque)" value={fmt(data.caisse.net) + ' FCFA'} sub={`${data.caisse.count} opérations`} accent="amber" />
            <KpiCard icon={<TrendingDown/>} label="Net Sage (BNQ)" value={fmt(data.sage.net) + ' FCFA'} sub={`${data.sage.count} écritures`} accent="emerald" />
            <KpiCard
              icon={data.rapproche ? <CheckCircle2/> : <AlertTriangle/>}
              label={data.rapproche ? '✓ Rapproché' : 'Écart détecté'}
              value={fmt(data.ecart) + ' FCFA'}
              accent={data.rapproche ? 'emerald' : 'amber'}
              data-testid="reconcile-ecart"
            />
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginTop: 18 }}>
            {/* Caisse */}
            <section className="v3-card v3-card-table">
              <div className="v3-card-head">
                <h3>Caisse (mode Banque)</h3>
                <span className="v3-card-sub">+{fmt(data.caisse.total_entrees)} · -{fmt(data.caisse.total_sorties)}</span>
              </div>
              <div className="v3-table-wrap">
                <table className="v3-table" data-testid="caisse-table">
                  <thead><tr><th>Date</th><th>Type</th><th>Bénéficiaire</th><th className="num">Montant</th></tr></thead>
                  <tbody>
                    {data.caisse.items.slice(0, 30).map((it) => (
                      <tr key={it.id} data-testid={`recon-caisse-${it.id}`}>
                        <td className="mono">{it.date}</td>
                        <td><span className={`v3-pill ${(it.type || '').toLowerCase().includes('entr') ? 'v3-pill-in' : 'v3-pill-out'}`}>{it.type}</span></td>
                        <td>{it.beneficiaire || it.categorie || '—'}</td>
                        <td className="num strong">{fmt(it.montant)}</td>
                      </tr>
                    ))}
                    {data.caisse.items.length === 0 && <tr><td colSpan={4} className="v3-empty">Aucune opération Banque.</td></tr>}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Sage */}
            <section className="v3-card v3-card-table">
              <div className="v3-card-head">
                <h3>Sage (journal BNQ)</h3>
                <span className="v3-card-sub">D {fmt(data.sage.total_debit)} · C {fmt(data.sage.total_credit)}</span>
              </div>
              <div className="v3-table-wrap">
                <table className="v3-table" data-testid="sage-table">
                  <thead><tr><th>Date</th><th>Pièce</th><th>Libellé</th><th className="num">Montant</th></tr></thead>
                  <tbody>
                    {data.sage.items.slice(0, 30).map((it) => (
                      <tr key={it.id} data-testid={`recon-sage-${it.id}`}>
                        <td className="mono">{it.date}</td>
                        <td className="mono">{it.piece || '—'}</td>
                        <td>{it.libelle}</td>
                        <td className="num strong">{fmt(it.montant)}</td>
                      </tr>
                    ))}
                    {data.sage.items.length === 0 && <tr><td colSpan={4} className="v3-empty">Aucune écriture BNQ.</td></tr>}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, sub, accent }) {
  return (
    <div className={`v3-kpi v3-kpi-${accent}`}>
      <div className="v3-kpi-icon">{icon}</div>
      <div className="v3-kpi-body">
        <div className="v3-kpi-label">{label}</div>
        <div className="v3-kpi-value">{value}</div>
        {sub && <div className="v3-kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}
